import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useToast } from '@vtex/admin-ui'
import { type DownloadItemProps } from '@/components/products-listing/components/DownloadCollapser/DownloadListCollapser'
import {
  type ExportData,
  type ExportType,
} from '@/components/products-listing/components/ExportMenu/ExportMenu'
import { useUserProfile } from '@/hooks/useUserProfile'
import { fetcher } from '@/lib/fetcher'
import { buildQuery } from '@/lib/transform'

interface UseExportRequestsProps {
  exportData: ExportData | null
  onCreateExport?: (newExport: DownloadItemProps) => void
}

export const useExportRequests = ({
  exportData,
  onCreateExport,
}: UseExportRequestsProps) => {
  const showToast = useToast()
  const { formatMessage } = useIntl()
  const { data: user } = useUserProfile(true)

  const [responses, setResponses] = useState<Record<string, DownloadItemProps>>(
    {}
  )
  const [actualHash, setActualHash] = useState<string>('')
  const criticalToastDuration = 3000

  useEffect(() => {
    if (
      actualHash !== '' &&
      responses[actualHash]?.Status !== 'Completed' &&
      responses[actualHash]?.Status !== 'Cancelled'
    ) {
      setTimeout(() => getExportRequest(actualHash), 2000)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responses, actualHash])

  const getExportRequest = async (lastHash: string) => {
    setActualHash(lastHash)
    try {
      const response = await fetcher({
        url: `/api/proxy/catalog/pvt/export/${lastHash}`,
      })

      setResponses(() => {
        const updated: any = {
          [lastHash]: response,
        }

        onCreateExport?.(updated)
        return updated
      })

      return responses
    } catch (_) {
      showToast({
        variant: 'critical',
        duration: criticalToastDuration,
        message: formatMessage({
          id: 'admin/admin.catalog.export-menu.unknown-error-export-request',
          defaultMessage:
            'Your file could not be processed due to a technical problem. Try again in a few moments or contact support if the issue persists.',
        }),
      })
    }
  }

  const postExportRequest = async (type: ExportType) => {
    const body = JSON.stringify(
      Object.fromEntries(
        Object.entries(exportData ?? {})
          .filter(
            ([key, value]) =>
              key && (Array.isArray(value) ? value.length > 0 : true)
          )
          .map(([key, value]) => [
            key,
            Array.isArray(value) ? value.map(String) : value,
          ])
      )
    )

    try {
      const response = await fetcher({
        url: `/api/proxy/catalog/pvt/admin/product/export/${type}?${buildQuery(
          ...Object.values((exportData ?? {}).filterQueries || {}).filter(
            (query) => !!query
          )
        )}&emailTo=${user?.email}`,
        method: 'POST',
        body,
      })

      showToast({
        variant: 'positive',
        duration: 1000,
        message: formatMessage({
          id: 'admin/admin.catalog.export-menu.successful-export-request',
          defaultMessage:
            "Export is being processed. You'll receive an email when the file is available for download.",
        }),
      })
      getExportRequest(response.HashLink)
    } catch (_) {
      showToast({
        variant: 'critical',
        duration: criticalToastDuration,
        message: formatMessage({
          id: 'admin/admin.catalog.export-menu.unknown-error-export-request',
          defaultMessage:
            'Your file could not be processed due to a technical problem. Try again in a few moments or contact support if the issue persists.',
        }),
      })
    }
  }

  const cancelExportRequest = async (hash: string) => {
    try {
      const response = await fetcher({
        url: `/api/proxy/catalog/pvt/report/${hash}/status`,
        method: 'PUT',
        body: JSON.stringify({
          Status: 'CancellationRequested',
        }),
      })

      getExportRequest(response.HashLink)
    } catch (_) {}
  }

  return { getExportRequest, postExportRequest, cancelExportRequest }
}
